import { Component, ElementRef, HostListener, inject, Input, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { FloorplanMonitoringItemDetailsModalComponent } from "@components/floorplan-monitoring-item-details-modal/floorplan-monitoring-item-details-modal.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { Device } from "@domain/product/monitoring/device";
import { MonitoringProductListAddService } from "@domain/project/monitoring-product-list/monitoring-product-list-add.service";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { MonitoringSearchItemComponent } from "@project/floorplanner/floorplan-menu/monitoring/monitoring-search-item/monitoring-search-item.component";
import { createMonitoringGroupedSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import { getMonitoringPrimaryLabel, isGroupedOption } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import {
  DeviceSearchItem,
  GroupSearchItem,
  MonitoringProductSearchService,
} from "@project/monitoring/monitoring-product-search.service";
import { MonitoringSearchResult } from "@project/monitoring/monitoring-search-result";
import { combineLatest, map } from "rxjs";

@Component({
  selector: "app-monitoring-product-list-search",
  templateUrl: "./monitoring-product-list-search.component.html",
  styleUrl: "./monitoring-product-list-search.component.scss",
  imports: [UiKitModule, FilterProductComponent, RefreshProductsHintComponent, MonitoringSearchItemComponent],
  standalone: true,
})
export class MonitoringProductListSearchComponent {
  private monitoringProductSearchService = inject(MonitoringProductSearchService);
  private modalService = inject(ModalService);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private monitoringProductListAddService = inject(MonitoringProductListAddService);

  @Input({ required: true }) set project(value: MonitoringProject) {
    this._project = value;
    this.filteredResults = [];
    this.searchValue = "";
    this.loadSearchData();
  }

  protected readonly canEditProductList = toSignal(
    combineLatest([this.userService.user$, this.projectService.hasUserWritePermission$]).pipe(
      map(([user, hasWritePermission]) => !user.readOnlyAccess && hasWritePermission === true),
    ),
    { initialValue: false },
  );

  private _project!: MonitoringProject;
  private groups: GroupSearchItem[] = [];
  private devicesWithContext: DeviceSearchItem[] = [];
  private deviceLookup = new Map<string, Device>();

  protected filteredResults: MonitoringSearchResult[] = [];
  protected searchValue = "";
  protected isOverlayOpen = false;
  @ViewChild("searchContainer") private searchContainer!: ElementRef;

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.searchContainer?.nativeElement.contains(event.target)) {
      this.isOverlayOpen = false;
    }
  }

  protected openOverlayIfHasValue(): void {
    if (this.searchValue) {
      this.isOverlayOpen = true;
    }
  }

  protected search(value: string): void {
    this.searchValue = value;
    if (!value) {
      this.filteredResults = [];
      this.isOverlayOpen = false;
      return;
    }
    this.filteredResults = this.monitoringProductSearchService.search(
      value,
      this.groups,
      this.devicesWithContext,
      this.deviceLookup,
    );
    this.isOverlayOpen = true;
  }

  protected addToProductList(result: MonitoringSearchResult): void {
    if (!this.canEditProductList()) return;
    this.monitoringProductListAddService.addToProductList(result, this._project, this.deviceLookup);
  }

  protected showGroupDetails(result: MonitoringSearchResult): void {
    if (!isGroupedOption(result.option)) return;
    const items: MonitoringGroupedPartSourceItem[] = [
      createMonitoringGroupedSourceItem({ option: result.option, quantity: 1 }, getMonitoringPrimaryLabel(result.category)),
    ];
    this.modalService.open(FloorplanMonitoringItemDetailsModalComponent, {
      size: ModalSize.LARGE,
      data: { items, category: result.category, title: result.option.groupName },
    });
  }

  private loadSearchData(): void {
    this.monitoringProductSearchService.loadSearchData().subscribe((data) => {
      this.groups = data.groups;
      this.devicesWithContext = data.devices;
      this.deviceLookup = data.lookup;
    });
  }
}
